import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import cn from 'classnames';
import clsx from 'clsx';
import { getAsPath } from 'react-scale/lib/utils';

const ArrowRight = () => (
  <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.83317 8H13.1665"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.49984 12.6667L13.1665 8.00004L8.49984 3.33337"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowLeft = () => (
  <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.1668 8H3.8335"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.50016 12.6667L3.8335 8.00004L8.50016 3.33337"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type Props = {
  pages: number;
  currentPage: number;
  isInPage: boolean;
  pageCallback?: (pageNumber: number) => void;
  className?: string;
};

const scrollToPageStart = () => {
  document.getElementById('page').scrollIntoView({
    behavior: 'smooth'
  });
};

export const Navigation = ({
  pages,
  currentPage,
  isInPage,
  pageCallback = scrollToPageStart,
  className
}: Props) => {
  const maxPageOffset = pages - 4;

  const [pageNavigationOffset, setPageNavigationOffset] = useState(0);

  const router = useRouter();

  useEffect(() => {
    if (currentPage < 0) {
      router.push(getAsPath(router.asPath, { pageNumber: '1' }), undefined, { scroll: false });
    }
  }, [currentPage, pages]);

  useEffect(() => {
    if (currentPage > pages - 1 && pages > 0) {
      router.push(getAsPath(router.asPath, { pageNumber: pages.toString() }), undefined, {
        scroll: false
      });
    }
  }, [currentPage, pages]);

  useEffect(() => {
    if (isInPage && !Number.isNaN(currentPage)) {
      if (currentPage <= 2) {
        setPageNavigationOffset(0);
      } else if (currentPage > maxPageOffset) {
        setPageNavigationOffset(maxPageOffset);
      } else {
        setPageNavigationOffset(currentPage - 2);
      }
    }
  }, [isInPage, currentPage, maxPageOffset]);

  async function handlePreviousPage() {
    await router.push(getAsPath(router.asPath, { pageNumber: currentPage.toString() }), undefined, {
      scroll: false
    });
    pageCallback(currentPage - 1);
  }

  async function handleNextPage() {
    await router.push(
      getAsPath(router.asPath, { pageNumber: (currentPage + 2).toString() }),
      undefined,
      {
        scroll: false
      }
    );
    pageCallback(currentPage + 1);
  }

  async function handlePageNumberClick(n: number) {
    await router.push(getAsPath(router.asPath, { pageNumber: (n + 1).toString() }), undefined, {
      scroll: false
    });
    pageCallback(n);
  }

  return (
    <div
      className={clsx(
        'flex justify-between items-center lg:mx-auto text-lg lg:max-w-max lg:space-x-5 whitespace-nowrap',
        className
      )}
    >
      <button
        type="button"
        className="flex items-center space-x-2 disabled:cursor-not-allowed disabled:opacity-30"
        onClick={handlePreviousPage}
        disabled={currentPage === 0}
      >
        <ArrowLeft />
        <span>Previous</span>
      </button>
      <div className="space-x-3 hidden lg:block">
        {[...Array(pages).keys()].map((_page, i) => {
          if (i < pageNavigationOffset - 4) return null;
          if (i > pageNavigationOffset + 8) return null;

          return (
            <button
              type="button"
              aria-label={`To page ${i}`}
              className={cn(currentPage === i ? 'text-scale-mauve underline' : 'text-[#888888]')}
              key={_page}
              onClick={() => handlePageNumberClick(i)}
              disabled={currentPage === i}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
      <button
        className="flex items-center space-x-2 disabled:cursor-not-allowed disabled:opacity-30"
        type="button"
        onClick={handleNextPage}
        disabled={currentPage + 1 === pages || pages < 1}
      >
        <span>Next</span>
        <ArrowRight />
      </button>
    </div>
  );
};
